import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { ActionPlan, EscalationRules } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite } from "../../../../contexts/SiteContext";
import { useLanguage } from "../../../../contexts";
import "./ActionPlanForm.scss";

const apController    = new ActionPlan();
const rulesController = new EscalationRules();

// ── Constantes Sección 1 ────────────────────────────────────────────────────

const FINDING_ORIGIN_OPTIONS = [
  { value: "internal_audit",     label: "Auditoría interna" },
  { value: "external_audit",     label: "Auditoría externa / cliente" },
  { value: "plant_inspection",   label: "Inspección de planta" },
  { value: "risk_assessment",    label: "Evaluación de riesgos" },
  { value: "accident_incident",  label: "Accidente / Incidente" },
  { value: "legal_requirement",  label: "Requisito legal incumplido" },
  { value: "safety_checklist",   label: "Check list de seguridad" },
  { value: "new_project",        label: "Nuevo proyecto / obra" },
  { value: "initial_diagnosis",  label: "Diagnóstico inicial" },
  { value: "own_initiative",     label: "Iniciativa propia" },
  { value: "client_requirement", label: "Requerimiento de cliente o marca" },
  { value: "other",              label: "Otro" },
];

const ORIGIN_MODULE_HINTS = {
  accident_incident: { module: "Accidentes",           text: "Generá esta acción desde el módulo Accidentes para vincularla al expediente de investigación." },
  internal_audit:    { module: "Auditorías",            text: "Generá esta acción desde el módulo Auditorías para vincular el hallazgo automáticamente." },
  external_audit:    { module: "Auditorías",            text: "Generá esta acción desde el módulo Auditorías (auditoría externa / cliente) para vincularla." },
  legal_requirement: { module: "Req. Legales",          text: "Vinculá esta acción al requisito incumplido en el módulo Requisitos Legales (bidireccional)." },
  risk_assessment:   { module: "Eval. de Riesgos",      text: "Generá esta acción desde la matriz de riesgos para vincular el nivel residual y sugerir prioridad automática." },
  safety_checklist:  { module: "Check List",            text: "Generá esta acción desde el ítem fallido en el módulo Check List para vincular la inspección." },
  new_project:       { module: "Obras",                 text: "Generá esta acción desde el módulo Obras para vincular el hallazgo del contratista u obra." },
};

const FINDING_TYPE_OPTIONS = [
  { value: "non_conformity",    label: "No conformidad" },
  { value: "observation",       label: "Observación" },
  { value: "improvement",       label: "Mejora" },
  { value: "preventive_action", label: "Acción preventiva" },
  { value: "corrective_action", label: "Acción correctiva" },
  { value: "legal_compliance",  label: "Acción de cumplimiento legal" },
];

const AREA_FUNCIONAL_OPTIONS = [
  "Seguridad",
  "Higiene Industrial",
  "Salud Ocupacional",
  "Medio Ambiente",
  "Capacitación",
  "Mantenimiento",
  "Producción",
  "Almacén",
  "Calidad",
  "Química / Sustancias Peligrosas",
  "Construcción y Obras",
  "Contratistas",
  "Emergencias",
];

const PRIORITY_OPTIONS = [
  { value: "high",   label: "Alta — Urgente",            color: "#e53935" },
  { value: "medium", label: "Media — Normal",             color: "#f9a825" },
  { value: "low",    label: "Baja — Mejora progresiva",   color: "#43a047" },
];

// ── Constantes Sección 4 ────────────────────────────────────────────────────

const ALERTA_OPTIONS = [
  { value: "7", label: "7 días" },
  { value: "5", label: "5 días" },
  { value: "3", label: "3 días" },
  { value: "1", label: "1 día"  },
  { value: "0", label: "Al vencer" },
];

const CHANNEL_OPTIONS = [
  { value: "email",   label: "Email"    },
  { value: "browser", label: "Navegador" },
];

const FILE_MAX_SIZE   = 5 * 1024 * 1024; // 5 MB
const FILE_ALLOWED    = ["image/", "application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

function isAllowedFile(file) {
  return FILE_ALLOWED.some((t) => file.type.startsWith(t));
}
function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// ── Constantes Sección 3 ────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ESCALATION_DEFAULTS = {
  high:   { rule_l1: 0, rule_l2: 2, rule_l3: 5  },
  medium: { rule_l1: 0, rule_l2: 3, rule_l3: 7  },
  low:    { rule_l1: 0, rule_l2: 5, rule_l3: 10 },
};

// ── Constantes Sección 2 ────────────────────────────────────────────────────

const STATUS_OPTIONS_MANUAL = [
  { value: "pending",              label: "Iniciada" },
  { value: "in_progress",         label: "En progreso" },
  { value: "implemented_pending", label: "Implementada — pendiente verificación" },
  { value: "closed",              label: "Cerrada / Verificada" },
  { value: "cancelled",           label: "Cancelada" },
];

const MAX_TITLE_CHARS = 200;
const MAX_DESC_CHARS  = 1000;
const MAX_NOTES_CHARS = 2000;

const today = () => new Date().toISOString().slice(0, 10);
const toDate = (d)  => d ? new Date(d).toISOString().slice(0, 10) : "";

function buildForm(ap, prefill, site) {
  return {
    // Sección 1
    title:              ap?.title              || prefill?.title              || "",
    findingOrigin:      ap?.findingOrigin      || prefill?.findingOrigin      || "plant_inspection",
    findingType:        ap?.findingType        || prefill?.findingType        || "non_conformity",
    findingDescription: ap?.findingDescription || prefill?.findingDescription || "",
    areaFuncional:      ap?.areaFuncional      || "Seguridad",
    priority:           ap?.priority           || prefill?.priority           || "medium",
    workPermitId:       ap?.workPermitId       || prefill?.workPermitId       || null,
    inspectionId:       ap?.inspectionId       || prefill?.inspectionId       || null,
    moduloOrigen:       ap?.moduloOrigen       || prefill?.moduloOrigen       || "",
    site:               site || ap?.site?._id  || ap?.site || "",
    // Sección 2
    status:           ap?.status           || "pending",
    openingDate:      toDate(ap?.openingDate)      || today(),
    plannedStartDate: toDate(ap?.plannedStartDate) || "",
    dueDate:          toDate(ap?.dueDate)           || "",
    closedDate:       toDate(ap?.closedDate)        || "",
    progress:         ap?.progress         ?? 0,
    notes:            ap?.notes            || "",
    // Sección 4
    alertas_preaviso: ap?.alertas_preaviso ?? ["3", "1", "0"],
    channels: ap?.channels ?? ["email", "browser"],
    // Sección 3
    resp1:       ap?.resp1       || "",
    resp1_email: ap?.resp1_email || "",
    resp2:       ap?.resp2       || "",
    resp2_email: ap?.resp2_email || "",
    resp3:       ap?.resp3       || "",
    resp3_email: ap?.resp3_email || "",
    rule_l1:     ap?.rule_l1     ?? (ESCALATION_DEFAULTS[ap?.priority || "medium"].rule_l1),
    rule_l2:     ap?.rule_l2     ?? (ESCALATION_DEFAULTS[ap?.priority || "medium"].rule_l2),
    rule_l3:     ap?.rule_l3     ?? (ESCALATION_DEFAULTS[ap?.priority || "medium"].rule_l3),
  };
}

function FieldLabel({ children, required }) {
  return (
    <label className="apf__label">
      {children}
      {required && <span className="apf__required"> *</span>}
    </label>
  );
}

// ── Componente principal ────────────────────────────────────────────────────

export function ActionPlanForm({ show, close, actionPlan, onSaved, prefill }) {
  const { accessToken } = useAuth();
  const { site }        = useSite();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const FINDING_ORIGIN_OPTS = [
    { value: "internal_audit",     label: t("origin_internal_audit")     },
    { value: "external_audit",     label: t("origin_external_audit")     },
    { value: "plant_inspection",   label: t("origin_plant_inspection")   },
    { value: "risk_assessment",    label: t("origin_risk_assessment")    },
    { value: "accident_incident",  label: t("origin_accident_incident")  },
    { value: "legal_requirement",  label: t("origin_legal_requirement")  },
    { value: "safety_checklist",   label: t("origin_safety_checklist")   },
    { value: "new_project",        label: t("origin_new_project")        },
    { value: "initial_diagnosis",  label: t("origin_initial_diagnosis")  },
    { value: "own_initiative",     label: t("origin_own_initiative")     },
    { value: "client_requirement", label: t("origin_client_requirement") },
    { value: "other",              label: t("origin_other")              },
  ];
  const FINDING_TYPE_OPTS = [
    { value: "non_conformity",    label: t("finding_type_non_conformity")    },
    { value: "observation",       label: t("finding_type_observation")       },
    { value: "improvement",       label: t("finding_type_improvement")       },
    { value: "preventive_action", label: t("finding_type_preventive_action") },
    { value: "corrective_action", label: t("finding_type_corrective_action") },
    { value: "legal_compliance",  label: t("finding_type_legal_compliance")  },
  ];
  const PRIORITY_OPTS = [
    { value: "high",   label: t("priority_high"),   color: "#e53935" },
    { value: "medium", label: t("priority_medium"), color: "#f9a825" },
    { value: "low",    label: t("priority_low"),    color: "#43a047" },
  ];
  const STATUS_OPTS_MANUAL = [
    { value: "pending",              label: t("ap_status_started")             },
    { value: "in_progress",         label: t("ap_status_in_progress")         },
    { value: "implemented_pending", label: t("ap_status_implemented_manual")  },
    { value: "closed",              label: t("ap_status_closed_verified")     },
    { value: "cancelled",           label: t("ap_status_cancelled")           },
  ];
  const ALERTA_OPTS = [
    { value: "7", label: t("alert_days_7")    },
    { value: "5", label: t("alert_days_5")    },
    { value: "3", label: t("alert_days_3")    },
    { value: "1", label: t("alert_days_1")    },
    { value: "0", label: t("alert_days_on_due") },
  ];

  const [form,        setForm]        = useState(() => buildForm(actionPlan, prefill, site));
  const [saving,      setSaving]      = useState(false);
  const [globalRules, setGlobalRules] = useState(null);
  const [ruleUpdateOffer, setRuleUpdateOffer] = useState(null);
  const [s1Open, setS1Open] = useState(true);
  const [s2Open, setS2Open] = useState(true);
  const [s3Open, setS3Open] = useState(true);
  const [s4Open, setS4Open] = useState(true);
  const [s5Open, setS5Open] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging,   setIsDragging]   = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setForm(buildForm(actionPlan, prefill, site));
    setRuleUpdateOffer(null);

    rulesController.getEscalationRules(accessToken)
      .then((fetched) => {
        setGlobalRules(fetched);
        if (!actionPlan) {
          setForm((p) => {
            const defs = fetched[p.priority] || ESCALATION_DEFAULTS[p.priority] || ESCALATION_DEFAULTS.medium;
            return { ...p, rule_l1: defs.rule_l1, rule_l2: defs.rule_l2, rule_l3: defs.rule_l3 };
          });
        }
      })
      .catch(() => {});
  }, [show, actionPlan, site]);

  const setField = useCallback((field, val) => setForm((p) => ({ ...p, [field]: val })), []);

  const titleLen = form.title.length;
  const descLen  = form.findingDescription.length;
  const notesLen = form.notes.length;

  const titleCountClass = "apf__char-count" + (titleLen > MAX_TITLE_CHARS * 0.9 ? " apf__char-count--warn" : "");
  const descCountClass  = "apf__char-count" + (descLen  > MAX_DESC_CHARS  * 0.9 ? " apf__char-count--warn" : "");
  const notesCountClass = "apf__char-count" + (notesLen > MAX_NOTES_CHARS * 0.9 ? " apf__char-count--warn" : "");
  const titleInputClass = "apf__input"      + (titleLen > MAX_TITLE_CHARS       ? " apf__input--error"     : "");

  const isOverdue = useMemo(() => {
    if (!form.dueDate || form.progress >= 100) return false;
    if (form.status === "closed" || form.status === "cancelled") return false;
    return new Date(form.dueDate) < new Date(today());
  }, [form.dueDate, form.progress, form.status]);

  const suggestClose = form.progress === 100 && form.status !== "closed" && form.status !== "cancelled";

  const handleStatusChange = useCallback((val) => {
    setForm((p) => ({
      ...p,
      status:     val,
      closedDate: val === "closed" && !p.closedDate ? today() : p.closedDate,
    }));
  }, []);

  const handleProgressChange = useCallback((val) => {
    const num = Number(val);
    setForm((p) => ({
      ...p,
      progress:   num,
      closedDate: num === 100 && p.status === "closed" && !p.closedDate ? today() : p.closedDate,
    }));
  }, []);

  const handleToggleArray = useCallback((field, val) => {
    setForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] };
    });
  }, []);

  const addFiles = useCallback((list) => {
    const valid = Array.from(list).filter((f) => {
      if (f.size > FILE_MAX_SIZE) { toast.warning(f.name + ": " + t("apf_file_size_hint")); return false; }
      if (!isAllowedFile(f))      { toast.warning(f.name + ": " + t("apf_file_type_hint")); return false; }
      return true;
    });
    setPendingFiles((prev) => [...prev, ...valid]);
  }, []);

  const handleFileSelect = useCallback((e) => { addFiles(e.target.files); e.target.value = ""; }, [addFiles]);
  const handleFileDrop   = useCallback((e) => {
    e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files);
  }, [addFiles]);
  const handleRemovePending = useCallback((idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);
  const handleDeleteUploaded = useCallback(async (key) => {
    if (!actionPlan?._id) return;
    try {
      await apController.deleteFile(accessToken, actionPlan._id, key);
      toast.success(t("apf_file_deleted"));
      onSaved?.();
    } catch { toast.error(t("apf_error_delete_file")); }
  }, [accessToken, actionPlan, onSaved]);

  const handlePriorityChange = useCallback((val) => {
    setForm((p) => {
      const src     = globalRules || ESCALATION_DEFAULTS;
      const prevDef = src[p.priority] || ESCALATION_DEFAULTS.medium;
      const newDef  = src[val]        || ESCALATION_DEFAULTS.medium;
      const rulesAreDefault =
        Number(p.rule_l1) === prevDef.rule_l1 &&
        Number(p.rule_l2) === prevDef.rule_l2 &&
        Number(p.rule_l3) === prevDef.rule_l3;
      if (rulesAreDefault) {
        setRuleUpdateOffer(null);
        return { ...p, priority: val, rule_l1: newDef.rule_l1, rule_l2: newDef.rule_l2, rule_l3: newDef.rule_l3 };
      }
      const label = t(`priority_${val}`) || val;
      setRuleUpdateOffer({ ...newDef, label });
      return { ...p, priority: val };
    });
  }, [globalRules]);

  const applyRuleDefaults = useCallback(() => {
    if (!ruleUpdateOffer) return;
    setForm((p) => ({ ...p, rule_l1: ruleUpdateOffer.rule_l1, rule_l2: ruleUpdateOffer.rule_l2, rule_l3: ruleUpdateOffer.rule_l3 }));
    setRuleUpdateOffer(null);
  }, [ruleUpdateOffer]);

  const dateErrors = useMemo(() => {
    const e = [];
    if (form.openingDate && form.dueDate && form.dueDate < form.openingDate)
      e.push(t("apf_err_due_before_open"));
    if (form.plannedStartDate && form.openingDate && form.plannedStartDate < form.openingDate)
      e.push(t("apf_err_start_before_open"));
    if (form.plannedStartDate && form.dueDate && form.plannedStartDate > form.dueDate)
      e.push(t("apf_err_start_after_due"));
    if (form.closedDate && form.openingDate && form.closedDate < form.openingDate)
      e.push(t("apf_err_closed_before_open"));
    return e;
  }, [form.openingDate, form.plannedStartDate, form.dueDate, form.closedDate]);

  const emailChannel = (form.channels || []).includes("email");

  const escalationErrors = useMemo(() => {
    const e = [];
    if (form.resp1_email && !EMAIL_REGEX.test(form.resp1_email)) e.push(t("apf_err_email_l1"));
    if (form.resp2_email && !EMAIL_REGEX.test(form.resp2_email)) e.push(t("apf_err_email_l2"));
    if (form.resp3_email && !EMAIL_REGEX.test(form.resp3_email)) e.push(t("apf_err_email_l3"));
    if (emailChannel && form.resp1 && !form.resp1_email) e.push(t("apf_err_missing_email_l1"));
    if (emailChannel && form.resp2 && !form.resp2_email) e.push(t("apf_err_missing_email_l2"));
    if (emailChannel && form.resp3 && !form.resp3_email) e.push(t("apf_err_missing_email_l3"));
    const l1 = Number(form.rule_l1), l2 = Number(form.rule_l2), l3 = Number(form.rule_l3);
    if (l2 <= l1) e.push(t("apf_err_rule_l2"));
    if (l3 <= l2) e.push(t("apf_err_rule_l3"));
    return e;
  }, [form.channels, form.resp1, form.resp1_email, form.resp2, form.resp2_email, form.resp3, form.resp3_email, form.rule_l1, form.rule_l2, form.rule_l3]);

  const legalClosureBlocked = useMemo(() => {
    if (form.findingType !== "legal_compliance") return false;
    if (form.status !== "closed") return false;
    const hasUploaded = actionPlan?.files && actionPlan.files.length > 0;
    const hasPending  = pendingFiles.length > 0;
    return !hasUploaded && !hasPending;
  }, [form.findingType, form.status, actionPlan, pendingFiles]);

  const validationErrors = useMemo(() => {
    const e = [];
    if (!form.title.trim())          e.push(t("apf_val_title"));
    if (!form.findingOrigin)         e.push(t("apf_val_finding_origin"));
    if (!form.findingType)           e.push(t("apf_val_finding_type"));
    if (!form.areaFuncional)         e.push(t("apf_val_area"));
    if (!form.priority)              e.push(t("apf_val_priority"));
    if (!form.dueDate)               e.push(t("apf_val_due_date"));
    if (!form.resp1.trim())          e.push(t("apf_val_resp1"));
    if (dateErrors.length > 0)       e.push(t("apf_val_dates"));
    if (escalationErrors.length > 0) e.push(t("apf_val_escalation"));
    if (legalClosureBlocked)         e.push(t("apf_val_legal_evidence"));
    return e;
  }, [form, dateErrors, escalationErrors, legalClosureBlocked]);

  const savingInFlightRef = useRef(false);
  const handleSave = async () => {
    if (validationErrors.length > 0) {
      toast.warning(t("apf_missing_fields") + " " + validationErrors.join(", ") + ".");
      return;
    }
    if (savingInFlightRef.current) return;
    savingInFlightRef.current = true;
    setSaving(true);
    try {
      const payload = { ...form, site: site || form.site || undefined };
      let savedId;
      if (actionPlan?._id) {
        await apController.updateActionPlan(accessToken, actionPlan._id, payload);
        savedId = actionPlan._id;
        toast.success(t("apf_action_updated"));
      } else {
        const result = await apController.createActionPlan(accessToken, payload);
        savedId = result.actionPlan._id;
        toast.success(t("apf_action_created"));
      }
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append("file", file);
        await apController.uploadFile(accessToken, savedId, fd);
      }
      setPendingFiles([]);
      onSaved?.();
      close();
    } catch (err) {
      toast.error(err?.msg || t("apf_error_save"));
    } finally {
      savingInFlightRef.current = false;
      setSaving(false);
    }
  };

  const isEdit = !!actionPlan?._id;

  if (!show) return null;

  return (
      <div className="apf">

        {/* ── Encabezado gradiente ── */}
        <div className="apf-drawer-header">
          <div className="apf-drawer-header__left">
            {isEdit && (
              <span className="apf-drawer-header__id-pill">
                {actionPlan._id.toString().slice(-6).toUpperCase()}
              </span>
            )}
            <div>
              <div className="apf-drawer-header__title">
                {isEdit ? t("apf_edit_title") : t("apf_new_title")}
              </div>
              <div className="apf-drawer-header__subtitle">
                {t("apf_required_hint")}
                {form.moduloOrigen && (
                  <span className="apf-drawer-header__origin-tag">
                    {t("apf_origin_from")} {t(`apf_mod_${form.moduloOrigen}`)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" className="apf-drawer-header__close-btn" onClick={close}>✕</button>
        </div>

        {/* ── Cuerpo ── */}
        <div className="apf__scroll">

          {/* ── SECCIÓN 1 — IDENTIFICACIÓN ── */}
          <div className="apf-section">
            <button type="button" className="apf-section__header" onClick={() => setS1Open((v) => !v)}>
              <span className={`apf-section__chevron${s1Open ? " open" : ""}`}>▾</span>
              <span className="apf-section__icon">🔍</span>
              <span className="apf-section__title">{t("apf_section_identification")}</span>
            </button>

            {s1Open && (
              <div className="apf-section__body">

                {/* 1.1 Título */}
                <div className="apf__field apf__field--full">
                  <FieldLabel required>{t("title")}</FieldLabel>
                  <input
                    type="text"
                    className={titleInputClass}
                    value={form.title}
                    maxLength={MAX_TITLE_CHARS}
                    placeholder="Nombre corto y descriptivo de la acción..."
                    onChange={(e) => setField("title", e.target.value)}
                  />
                  <span className={titleCountClass}>{titleLen}/{MAX_TITLE_CHARS}</span>
                </div>

                {/* Row: Origen + Tipo */}
                <div className="apf__row-2">

                  {/* 1.2 Origen del hallazgo */}
                  <div className="apf__field">
                    <FieldLabel required>{t("apf_finding_origin_label")}</FieldLabel>
                    <select
                      className="apf__select"
                      value={form.findingOrigin}
                      onChange={(e) => setField("findingOrigin", e.target.value)}
                    >
                      <option value="">{t("apf_select_option")}</option>
                      {FINDING_ORIGIN_OPTS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {ORIGIN_MODULE_HINTS[form.findingOrigin] && (
                      <p className="apf__hint apf__hint--module">
                        💡 <strong>{ORIGIN_MODULE_HINTS[form.findingOrigin].module}:</strong>{" "}
                        {ORIGIN_MODULE_HINTS[form.findingOrigin].text}
                      </p>
                    )}
                  </div>

                  {/* 1.3 Tipo de hallazgo */}
                  <div className="apf__field">
                    <FieldLabel required>{t("apf_finding_type_label")}</FieldLabel>
                    <select
                      className="apf__select"
                      value={form.findingType}
                      onChange={(e) => setField("findingType", e.target.value)}
                    >
                      <option value="">{t("apf_select_option")}</option>
                      {FINDING_TYPE_OPTS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {form.findingType === "legal_compliance" && (
                      <p className={legalClosureBlocked ? "apf__hint apf__hint--legal-block" : "apf__hint apf__hint--warn"}>
                        {legalClosureBlocked
                          ? t("apf_legal_block_hint")
                          : t("apf_legal_warn_hint")}
                      </p>
                    )}
                    {ruleUpdateOffer && (
                      <p className="apf__hint apf__hint--info">
                        {t("apf_custom_rules_hint")} {ruleUpdateOffer.label}?
                        (L1={ruleUpdateOffer.rule_l1}d · L2={ruleUpdateOffer.rule_l2}d · L3={ruleUpdateOffer.rule_l3}d){" "}
                        <button type="button" className="apf__inline-link" onClick={applyRuleDefaults}>{t("apf_yes_replace")}</button>
                        {" · "}
                        <button type="button" className="apf__inline-link" onClick={() => setRuleUpdateOffer(null)}>{t("apf_keep")}</button>
                      </p>
                    )}
                  </div>
                </div>

                {/* 1.4 Descripción */}
                <div className="apf__field apf__field--full">
                  <FieldLabel>{t("apf_finding_description_label")}</FieldLabel>
                  <textarea
                    className="apf__textarea"
                    rows={4}
                    maxLength={MAX_DESC_CHARS}
                    placeholder="Detalle del hallazgo: condición observada, contexto, riesgo asociado, ubicación específica..."
                    value={form.findingDescription}
                    onChange={(e) => setField("findingDescription", e.target.value)}
                  />
                  <span className={descCountClass}>{descLen}/{MAX_DESC_CHARS}</span>
                </div>

                {/* Row: Área + Prioridad */}
                <div className="apf__row-2">

                  {/* 1.5 Área / Sector */}
                  <div className="apf__field">
                    <FieldLabel required>{t("apf_area_sector_label")}</FieldLabel>
                    <select
                      className="apf__select"
                      value={form.areaFuncional}
                      onChange={(e) => setField("areaFuncional", e.target.value)}
                    >
                      <option value="">{t("apf_select_option")}</option>
                      {AREA_FUNCIONAL_OPTIONS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* 1.6 Prioridad */}
                  <div className="apf__field">
                    <FieldLabel required>{t("priority")}</FieldLabel>
                    <select
                      className="apf__select"
                      value={form.priority}
                      onChange={(e) => handlePriorityChange(e.target.value)}
                      style={{ color: PRIORITY_OPTS.find((p) => p.value === form.priority)?.color }}
                    >
                      <option value="">{t("apf_select_option")}</option>
                      {PRIORITY_OPTS.map((p) => (
                        <option key={p.value} value={p.value} style={{ color: p.color }}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>
            )}
          </div>

          {/* ── SECCIÓN 2 — SEGUIMIENTO ── */}
          <div className="apf-section">
            <button type="button" className="apf-section__header" onClick={() => setS2Open((v) => !v)}>
              <span className={`apf-section__chevron${s2Open ? " open" : ""}`}>▾</span>
              <span className="apf-section__icon">📋</span>
              <span className="apf-section__title">{t("apf_section_followup")}</span>
            </button>

            {s2Open && (
              <div className="apf-section__body">

                {/* Row: Estado | Fecha apertura */}
                <div className="apf__row-2">
                  <div className="apf__field">
                    <FieldLabel required>{t("apf_status_label")}</FieldLabel>
                    <div className="apf__status-row">
                      <select
                        className="apf__select"
                        value={form.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        {STATUS_OPTS_MANUAL.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {isOverdue && (
                        <span className="apf__overdue-badge">{t("apf_overdue_badge")}</span>
                      )}
                    </div>
                    {suggestClose && (
                      <p className="apf__hint apf__hint--info">
                        {t("apf_progress_100_hint")}{" "}
                        <button type="button" className="apf__inline-link"
                          onClick={() => handleStatusChange("closed")}>
                          {t("apf_mark_closed_btn")}
                        </button>
                      </p>
                    )}
                  </div>
                  <div className="apf__field">
                    <FieldLabel>{t("apf_opening_date_label")}</FieldLabel>
                    <input type="date" className="apf__input"
                      value={form.openingDate}
                      onChange={(e) => setField("openingDate", e.target.value)} />
                  </div>
                </div>

                {/* Row: Inicio planificado | Vencimiento */}
                <div className="apf__row-2">
                  <div className="apf__field">
                    <FieldLabel>{t("apf_planned_start_label")}</FieldLabel>
                    <input type="date" className="apf__input"
                      value={form.plannedStartDate}
                      onChange={(e) => setField("plannedStartDate", e.target.value)} />
                  </div>

                  <div className="apf__field">
                    <FieldLabel required>{t("due_date")}</FieldLabel>
                    <input type="date" className="apf__input"
                      value={form.dueDate}
                      onChange={(e) => setField("dueDate", e.target.value)} />
                  </div>
                </div>

                {/* Cierre real — solo si estado = cerrada */}
                {form.status === "closed" && (
                  <div className="apf__field">
                    <FieldLabel>{t("apf_closed_date_label")}</FieldLabel>
                    <input type="date" className="apf__input"
                      value={form.closedDate}
                      onChange={(e) => setField("closedDate", e.target.value)} />
                  </div>
                )}

                {/* Errores de fechas */}
                {dateErrors.length > 0 && (
                  <div className="apf__date-errors">
                    {dateErrors.map((e, i) => <p key={i}>⚠ {e}</p>)}
                  </div>
                )}

                {/* % Avance */}
                <div className="apf__field apf__field--full">
                  <FieldLabel>{t("apf_progress_label")}</FieldLabel>
                  <div className="apf__progress-row">
                    <input
                      type="range" min={0} max={100} step={5}
                      className="apf__range"
                      value={form.progress}
                      onChange={(e) => handleProgressChange(e.target.value)}
                    />
                    <span className="apf__progress-val">{form.progress}%</span>
                  </div>
                </div>

                {/* Observaciones / notas */}
                <div className="apf__field apf__field--full">
                  <FieldLabel>{t("observations")}</FieldLabel>
                  <textarea
                    className="apf__textarea"
                    rows={3}
                    maxLength={MAX_NOTES_CHARS}
                    placeholder="Notas del responsable: avances, obstáculos, lecciones aprendidas..."
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                  <span className={notesCountClass}>{notesLen}/{MAX_NOTES_CHARS}</span>
                </div>

              </div>
            )}
          </div>

          {/* ── SECCIÓN 3 — ESCALACIÓN ── */}
          <div className="apf-section">
            <button type="button" className="apf-section__header" onClick={() => setS3Open((v) => !v)}>
              <span className={`apf-section__chevron${s3Open ? " open" : ""}`}>▾</span>
              <span className="apf-section__icon">📡</span>
              <span className="apf-section__title">{t("apf_section_escalation")}</span>
            </button>

            {s3Open && (
              <div className="apf-section__body">

                {/* Nivel 1 */}
                <div className="apf__escalation-level">
                  <div className="apf__escalation-badge apf__escalation-badge--1">{t("apf_esc_level_1_badge")}</div>
                  <div className="apf__row-2">
                    <div className="apf__field">
                      <FieldLabel required>{t("apf_resp_name_label")}</FieldLabel>
                      <input
                        type="text"
                        className="apf__input"
                        placeholder="Nombre del responsable directo..."
                        value={form.resp1}
                        onChange={(e) => setField("resp1", e.target.value)}
                      />
                    </div>
                    <div className="apf__field">
                      <FieldLabel required={emailChannel}>Email</FieldLabel>
                      <input
                        type="email"
                        className={"apf__input" + (form.resp1_email && !EMAIL_REGEX.test(form.resp1_email) ? " apf__input--error" : "")}
                        placeholder="correo@empresa.com"
                        value={form.resp1_email}
                        onChange={(e) => setField("resp1_email", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Nivel 2 */}
                <div className="apf__escalation-level">
                  <div className="apf__escalation-badge apf__escalation-badge--2">{t("apf_esc_level_2_badge")}</div>
                  <div className="apf__row-2">
                    <div className="apf__field">
                      <FieldLabel>{t("apf_direct_manager_label")}</FieldLabel>
                      <input
                        type="text"
                        className="apf__input"
                        placeholder="Si está vacío, la escalación N2 no se dispara."
                        value={form.resp2}
                        onChange={(e) => setField("resp2", e.target.value)}
                      />
                    </div>
                    <div className="apf__field">
                      <FieldLabel required={emailChannel}>Email</FieldLabel>
                      <input
                        type="email"
                        className={"apf__input" + (form.resp2_email && !EMAIL_REGEX.test(form.resp2_email) ? " apf__input--error" : "")}
                        placeholder="correo@empresa.com"
                        value={form.resp2_email}
                        onChange={(e) => setField("resp2_email", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Nivel 3 */}
                <div className="apf__escalation-level">
                  <div className="apf__escalation-badge apf__escalation-badge--3">
                    {t("apf_esc_level_3_badge")}
                    {form.priority === "high" && <span className="apf__badge-hint"> — recomendado para prioridad Alta</span>}
                  </div>
                  <div className="apf__row-2">
                    <div className="apf__field">
                      <FieldLabel>{t("apf_management_label")}</FieldLabel>
                      <input
                        type="text"
                        className="apf__input"
                        placeholder="Si está vacío, la escalación N3 no se dispara."
                        value={form.resp3}
                        onChange={(e) => setField("resp3", e.target.value)}
                      />
                    </div>
                    <div className="apf__field">
                      <FieldLabel required={emailChannel}>Email</FieldLabel>
                      <input
                        type="email"
                        className={"apf__input" + (form.resp3_email && !EMAIL_REGEX.test(form.resp3_email) ? " apf__input--error" : "")}
                        placeholder="correo@empresa.com"
                        value={form.resp3_email}
                        onChange={(e) => setField("resp3_email", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Reglas L1 / L2 / L3 */}
                <div className="apf__field apf__field--full">
                  <FieldLabel required>{t("apf_esc_rules_label")}</FieldLabel>
                  <div className="apf__rules-row">
                    <div className="apf__rule-field">
                      <span className="apf__rule-label">L1 (días)</span>
                      <input
                        type="number" min={0} max={90}
                        className="apf__input apf__input--rule"
                        value={form.rule_l1}
                        onChange={(e) => setField("rule_l1", Number(e.target.value))}
                      />
                      <span className="apf__hint">{t("apf_hint_l1")}</span>
                    </div>
                    <div className="apf__rule-sep">→</div>
                    <div className="apf__rule-field">
                      <span className="apf__rule-label">L2 (días)</span>
                      <input
                        type="number" min={1} max={90}
                        className="apf__input apf__input--rule"
                        value={form.rule_l2}
                        onChange={(e) => setField("rule_l2", Number(e.target.value))}
                      />
                      <span className="apf__hint">{t("apf_hint_l2")}</span>
                    </div>
                    <div className="apf__rule-sep">→</div>
                    <div className="apf__rule-field">
                      <span className="apf__rule-label">L3 (días)</span>
                      <input
                        type="number" min={1} max={90}
                        className="apf__input apf__input--rule"
                        value={form.rule_l3}
                        onChange={(e) => setField("rule_l3", Number(e.target.value))}
                      />
                      <span className="apf__hint">{t("apf_hint_l3")}</span>
                    </div>
                  </div>
                  <p className="apf__hint">{t("apf_hint_rules_values")}</p>
                </div>

                {/* Errores de escalación */}
                {escalationErrors.length > 0 && (
                  <div className="apf__date-errors">
                    {escalationErrors.map((e, i) => <p key={i}>⚠ {e}</p>)}
                  </div>
                )}

              </div>
            )}
          </div>

          {/* ── SECCIÓN 4 — PRE-AVISOS Y CANALES ── */}
          <div className="apf-section">
            <button type="button" className="apf-section__header" onClick={() => setS4Open((v) => !v)}>
              <span className={`apf-section__chevron${s4Open ? " open" : ""}`}>▾</span>
              <span className="apf-section__icon">🔔</span>
              <span className="apf-section__title">{t("apf_section_alerts")}</span>
            </button>

            {s4Open && (
              <div className="apf-section__body">

                {/* 4.1 Pre-avisos */}
                <div className="apf__field apf__field--full">
                  <FieldLabel>{t("apf_alert_days_label")}</FieldLabel>
                  <div className="apf__pills">
                    {ALERTA_OPTS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={"apf__pill" + ((form.alertas_preaviso || []).includes(opt.value) ? " apf__pill--active" : "")}
                        onClick={() => handleToggleArray("alertas_preaviso", opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="apf__hint">Notificaciones preventivas al Nivel 1 antes de vencer.</p>
                </div>

                {/* 4.2 Canales */}
                <div className="apf__field apf__field--full">
                  <FieldLabel required>{t("apf_channels_label")}</FieldLabel>
                  <div className="apf__pills">
                    {CHANNEL_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={"apf__pill" + ((form.channels || []).includes(opt.value) ? " apf__pill--active" : "")}
                        onClick={() => handleToggleArray("channels", opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {(form.channels || []).includes("email") && (
                    <p className="apf__hint">{t("apf_email_active_hint")}</p>
                  )}
                </div>

              </div>
            )}
          </div>

        </div>{/* /apf__scroll */}

        {/* ── Acciones ── */}
        <div className="apf__actions">
          <div className="apf__actions__validation">
            {validationErrors.length > 0 && (
              <span className="apf__actions__validation-text">
                &#9651; {t("apf_missing_fields")} {validationErrors.join(", ")}
              </span>
            )}
          </div>
          <div className="apf__actions__buttons">
            <button
              type="button"
              className="apf__actions__btn apf__actions__btn--primary"
              onClick={handleSave}
              disabled={saving || validationErrors.length > 0}
            >
              {saving ? t("apf_btn_saving") : isEdit ? t("apf_btn_save_changes") : t("apf_btn_create")}
            </button>
            <button
              type="button"
              className="apf__actions__btn"
              onClick={close}
              disabled={saving}
            >
              {t("cancel")}
            </button>
          </div>
        </div>

      </div>
  );
}
